import type { IDCrypto } from '../types/iddwn-crypto.js';
import type { BbsKeyPair } from '../crypto-primitives/bbs.js';

import { Bbs } from '../crypto-primitives/bbs.js';
import { CryptoKey } from '../algorithms-api/index.js';

export interface BbsGenerateKeyOptions extends IDCrypto.Algorithm {
  name: 'BBS';
}

export interface BbsSignOptions extends IDCrypto.Algorithm {
  name: 'BBS';
}

/**
 * High-level BBS+ signature algorithm implementing multi-message signing,
 * verification, and zero-knowledge selective disclosure proof operations.
 *
 * Unlike EdDSA/ECDSA which sign a single data buffer, BBS+ signs an array
 * of messages and supports deriving proofs that reveal only a subset.
 */
export class BbsAlgorithm {
  public readonly name = 'BBS';

  public readonly keyUsages: IDCrypto.KeyPairUsage = {
    privateKey: ['sign'],
    publicKey: ['verify'],
  };

  /**
   * Generates a BLS12-381 G2 key pair for BBS+ operations.
   */
  public async generateKey(): Promise<IDCrypto.CryptoKeyPair> {
    const keyPair = await Bbs.generateKeyPair();

    const algorithm: BbsGenerateKeyOptions = { name: 'BBS' };

    const cryptoKeyPair: IDCrypto.CryptoKeyPair = {
      privateKey: new CryptoKey(algorithm, true, keyPair.secretKey, 'private', ['sign']),
      publicKey: new CryptoKey(algorithm, true, keyPair.publicKey, 'public', ['verify']),
    };

    return cryptoKeyPair;
  }

  /**
   * Signs multiple messages with BBS+ producing a single signature.
   *
   * @param options.keyPair - Raw BBS key pair (publicKey + secretKey as Uint8Array).
   * @param options.messages - Array of messages to sign.
   * @returns The BBS+ signature.
   */
  public async sign(options: {
    keyPair: BbsKeyPair;
    messages: Uint8Array[];
  }): Promise<Uint8Array> {
    return Bbs.sign(options);
  }

  /**
   * Verifies a BBS+ signature against the full message set.
   *
   * @param options.publicKey - The issuer's BLS12-381 G2 public key.
   * @param options.signature - The BBS+ signature to verify.
   * @param options.messages - The complete set of signed messages.
   */
  public async verify(options: {
    publicKey: Uint8Array;
    signature: Uint8Array;
    messages: Uint8Array[];
  }): Promise<boolean> {
    return Bbs.verify(options);
  }

  /**
   * Derives a zero-knowledge proof revealing only selected messages.
   *
   * @param options.publicKey - Issuer's public key.
   * @param options.signature - Original BBS+ signature.
   * @param options.messages - Complete message set.
   * @param options.revealed - Indices of messages to disclose.
   * @param options.nonce - Session-binding nonce.
   */
  public async createProof(options: {
    publicKey: Uint8Array;
    signature: Uint8Array;
    messages: Uint8Array[];
    revealed: number[];
    nonce: Uint8Array;
  }): Promise<Uint8Array> {
    return Bbs.createProof(options);
  }

  /**
   * Verifies a BBS+ selective disclosure proof.
   *
   * @param options.publicKey - Issuer's public key.
   * @param options.proof - The derived proof.
   * @param options.messages - Only the disclosed messages.
   * @param options.nonce - The nonce used during proof creation.
   */
  public async verifyProof(options: {
    publicKey: Uint8Array;
    proof: Uint8Array;
    messages: Uint8Array[];
    nonce: Uint8Array;
  }): Promise<boolean> {
    return Bbs.verifyProof(options);
  }
}
